/* Chess hero: a cursor-brightened chessboard woven into the hero background.
 *
 * The canvas draws the same checkerboard as the page-wide .vs-chess-page
 * conic texture (84px tile → 42px squares): same cell size, same grid
 * anchoring, same parity, measured in page coordinates — so the hero's board
 * is flush with the screen edges and continues seamlessly across the hero's
 * bottom boundary into the section below (the hero's bottom edge is snapped
 * onto the grid, and chess.css eases the green out over the last rows).
 * Filled squares sit at a low ambient alpha; each carries an intensity that
 * rises quickly while the pointer is near and decays slowly after it moves
 * on, so the cursor drags a brightening trail across the board. A feathered
 * per-pixel mask erases the board under the hero copy so the text stays
 * readable without a hard-edged dim box; with no pointer (page load, touch
 * devices) a slow Lissajous path wanders the brightening around instead.
 */

const CELL = 42;             // half the .vs-chess-page conic tile (84px) — same board
const RADIUS = 150;          // brighten radius around the pointer, px
const BASE_ALPHA = 0.045;    // ambient alpha of the always-on checkerboard
const PEAK_ALPHA = 0.20;     // fill alpha of a fully-brightened square
const TEXT_DIM = 0.25;       // board visibility multiplier under the hero copy
const TEXT_FADE = 90;        // px over which the dimming feathers back to full
const RISE_TAU = 60;         // ms — approach time constant while brightening
const FALL_TAU = 450;        // ms — decay time constant once the pointer moves on
const IDLE_RESUME_MS = 4000; // pointer gone this long → resume the idle wander

// Elements whose footprint dims the board beneath them.
const DIM_SELECTOR = '.vs-hero__copy .vs-eyebrow, .vs-hero__copy .vs-hero__title, ' +
    '.vs-hero__copy .vs-hero__sub, .vs-hero__copy .vs-btn';
const DIM_PAD = 12; // px of fully-dimmed breathing room before the feather starts

export function initChessHero() {
    const canvas = document.getElementById('vs-chess-hero-canvas');
    const section = canvas?.closest('.vs-hero');
    if (!canvas || !section) return;

    const ctx = canvas.getContext('2d');
    // The board lives on the page grid: page-space cell (i, j) spans
    // [i*CELL, (i+1)*CELL) × [j*CELL, (j+1)*CELL) from .vs-chess-page's
    // top-left. The page's repeating-conic-gradient darkens the (i + j) odd
    // squares, so the hero brightens the EVEN ones — odd squares read as the
    // board's dark squares on both sides of the boundary. (i0, j0) is the
    // first cell intersecting the canvas; (offX, offY) maps page → canvas.
    let w = 0, h = 0, cols = 0, rows = 0, i0 = 0, j0 = 0, offX = 0, offY = 0;
    let intensity = new Float32Array(0);

    // Feathered text protection, rebuilt on layout changes: the mask's alpha
    // is how much board to erase at each pixel — (1 - TEXT_DIM) inside an
    // element's padded rect, easing back to 0 over TEXT_FADE px of distance
    // (smoothstep of the distance to the nearest rect, so corners round off
    // and neighbouring elements blend into one soft region). Composited
    // after the squares with 'destination-out', which makes the dimming
    // per-pixel smooth instead of quantized to whole board cells.
    const mask = document.createElement('canvas');
    const maskCtx = mask.getContext('2d');

    function buildMask(sectionRect) {
        mask.width = Math.max(1, Math.round(w));
        mask.height = Math.max(1, Math.round(h));
        maskCtx.clearRect(0, 0, mask.width, mask.height);
        const rects = [];
        for (const el of section.querySelectorAll(DIM_SELECTOR)) {
            const r = el.getBoundingClientRect();
            rects.push({
                left: r.left - sectionRect.left - DIM_PAD,
                right: r.right - sectionRect.left + DIM_PAD,
                top: r.top - sectionRect.top - DIM_PAD,
                bottom: r.bottom - sectionRect.top + DIM_PAD,
            });
        }
        if (!rects.length) return;
        // Pixels further than TEXT_FADE from every rect stay fully clear, so
        // only the expanded union bounding box needs computing.
        const x0 = Math.max(0, Math.floor(Math.min(...rects.map(r => r.left)) - TEXT_FADE));
        const y0 = Math.max(0, Math.floor(Math.min(...rects.map(r => r.top)) - TEXT_FADE));
        const x1 = Math.min(mask.width, Math.ceil(Math.max(...rects.map(r => r.right)) + TEXT_FADE));
        const y1 = Math.min(mask.height, Math.ceil(Math.max(...rects.map(r => r.bottom)) + TEXT_FADE));
        if (x1 <= x0 || y1 <= y0) return;
        const img = maskCtx.createImageData(x1 - x0, y1 - y0);
        const data = img.data;
        for (let y = y0; y < y1; y++) {
            for (let x = x0; x < x1; x++) {
                let best = TEXT_FADE;
                for (const r of rects) {
                    const dx = Math.max(r.left - x, 0, x - r.right);
                    const dy = Math.max(r.top - y, 0, y - r.bottom);
                    const d = Math.sqrt(dx * dx + dy * dy);
                    if (d < best) best = d;
                }
                let t = best / TEXT_FADE;
                t = t * t * (3 - 2 * t);
                data[((y - y0) * (x1 - x0) + (x - x0)) * 4 + 3] =
                    Math.round((1 - TEXT_DIM) * (1 - t) * 255);
            }
        }
        maskCtx.putImageData(img, x0, y0);
    }

    function applyMask() {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.drawImage(mask, 0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';
    }

    function measure() {
        // Snap the hero's bottom edge onto the page grid so the CSS fade band
        // (the hero's last N*CELL px — see chess.css) covers whole rows of
        // squares. The 0.5px tolerance keeps ResizeObserver from re-snapping
        // over subpixel jitter.
        section.style.paddingBottom = '';
        const page = section.closest('.vs-chess-page') || document.body;
        let rect = section.getBoundingClientRect();
        const pageRect = page.getBoundingClientRect();
        const overhang = (rect.bottom - pageRect.top) % CELL;
        if (overhang > 0.5 && overhang < CELL - 0.5) {
            section.style.paddingBottom = `${CELL - overhang}px`;
            rect = section.getBoundingClientRect();
        }
        offX = rect.left - pageRect.left;
        offY = rect.top - pageRect.top;
        w = rect.width;
        h = rect.height;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        i0 = Math.floor(offX / CELL);
        j0 = Math.floor(offY / CELL);
        cols = Math.ceil((offX + w) / CELL) - i0;
        rows = Math.ceil((offY + h) / CELL) - j0;
        intensity = new Float32Array(cols * rows);
        buildMask(rect);
    }

    // Reduced motion: the ambient board is part of the design now, so paint
    // it statically (re-painting on layout changes) and skip the animation.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const drawBase = () => {
            ctx.clearRect(0, 0, w, h);
            ctx.fillStyle = `rgba(255,255,255,${BASE_ALPHA})`;
            for (let j = 0; j < rows; j++) {
                for (let i = 0; i < cols; i++) {
                    if ((i0 + i + j0 + j) & 1) continue;
                    ctx.fillRect((i0 + i) * CELL - offX, (j0 + j) * CELL - offY, CELL, CELL);
                }
            }
            applyMask();
        };
        const paint = () => { measure(); drawBase(); };
        new ResizeObserver(paint).observe(section);
        if (document.fonts?.ready) document.fonts.ready.then(paint);
        paint();
        return;
    }

    // Virtual pointer: the real cursor while it's over the hero, an idle
    // Lissajous wander otherwise (after a short grace period so the trail
    // fades out before the wander takes over).
    let px = -1e5, py = -1e5;
    let live = false;
    let lastReal = -1e9;

    section.addEventListener('pointermove', (e) => {
        const rect = section.getBoundingClientRect();
        px = e.clientX - rect.left;
        py = e.clientY - rect.top;
        live = true;
        lastReal = performance.now();
    }, { passive: true });
    section.addEventListener('pointerleave', () => {
        live = false;
        lastReal = performance.now();
    });

    let raf = 0;
    let running = false;
    let lastT = 0;

    function frame(t) {
        if (!running) return;
        raf = requestAnimationFrame(frame);
        const dt = Math.min(t - lastT, 50);
        lastT = t;

        let ix = px, iy = py;
        if (!live) {
            if (t - lastReal > IDLE_RESUME_MS) {
                ix = w * (0.5 + 0.38 * Math.sin(t * 0.00019 + 1.2));
                iy = h * (0.5 + 0.33 * Math.sin(t * 0.00027));
            } else {
                ix = -1e5; // no target: everything decays to ambient
                iy = -1e5;
            }
        }

        const kUp = 1 - Math.exp(-dt / RISE_TAU);
        const kDown = 1 - Math.exp(-dt / FALL_TAU);

        ctx.clearRect(0, 0, w, h);
        for (let j = 0; j < rows; j++) {
            const cy = (j0 + j) * CELL + CELL / 2 - offY;
            for (let i = 0; i < cols; i++) {
                if ((i0 + i + j0 + j) & 1) continue; // odd squares stay dark, as below
                const k = j * cols + i;
                const dx = (i0 + i) * CELL + CELL / 2 - offX - ix;
                const dy = cy - iy;
                const d = Math.sqrt(dx * dx + dy * dy);
                let target = Math.max(0, 1 - d / RADIUS);
                target *= target;
                const cur = intensity[k];
                let v = cur + (target - cur) * (target > cur ? kUp : kDown);
                if (v < 0.003) v = 0;
                intensity[k] = v;
                // Ambient board is always on; the pointer brightens it toward the peak.
                const alpha = BASE_ALPHA + v * (PEAK_ALPHA - BASE_ALPHA);
                ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(4)})`;
                ctx.fillRect((i0 + i) * CELL - offX, (j0 + j) * CELL - offY, CELL, CELL);
            }
        }
        applyMask();
    }

    function start() {
        if (running) return;
        running = true;
        lastT = performance.now();
        raf = requestAnimationFrame(frame);
    }
    function stop() {
        running = false;
        cancelAnimationFrame(raf);
    }

    // Only animate while the hero is actually on screen.
    new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) start(); else stop();
    }).observe(section);

    new ResizeObserver(() => measure()).observe(section);
    measure();
    // Re-measure the dim rects once web fonts settle (the title reflows).
    if (document.fonts?.ready) document.fonts.ready.then(measure);
}
